import { Injectable, NgZone } from '@angular/core';

declare global {
  interface Window {
    chatwootSettings: any;
    chatwootSDK: any;
    $chatwoot: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class ChatwootSdkService {
  private loaded = false;
  private loadFailed = false;

  constructor(private ngZone: NgZone) {}

  get isLoaded(): boolean {
    return !!window.$chatwoot;
  }

  loadSdk(baseUrl: string, websiteToken: string): Promise<void> {
    if (this.loaded) return Promise.resolve();
    if (this.loadFailed) {
      return Promise.reject(new Error('Chatwoot SDK previously failed to load'));
    }

    // Executa FORA da zona do Angular para que as operações
    // assíncronas do SDK Vue não disparem change detection
    return this.ngZone.runOutsideAngular(() => {
      return new Promise<void>((resolve, reject) => {
        const SDK_TIMEOUT_MS = 15000;

        window.chatwootSettings = {
          hideMessageBubble: true,
          position: 'right',
          locale: 'pt_BR',
          type: 'standard',
        };

        const cleanup = () => {
          window.removeEventListener('chatwoot:ready', onReady);
          clearTimeout(timeout);
        };

        const timeout = setTimeout(() => {
          cleanup();
          this.loadFailed = true;
          reject(new Error('Chatwoot SDK load timeout'));
        }, SDK_TIMEOUT_MS);

        const onReady = () => {
          cleanup();
          this.loaded = true;
          resolve();
        };

        window.addEventListener('chatwoot:ready', onReady);

        const script = document.createElement('script');
        script.src = `${baseUrl}/packs/js/sdk.js`;
        script.async = true;
        script.defer = true;

        script.onerror = () => {
          cleanup();
          this.loadFailed = true;
          reject(new Error('Failed to load Chatwoot SDK'));
        };

        script.onload = () => {
          if (window.chatwootSDK) {
            window.chatwootSDK.run({ websiteToken, baseUrl });
          } else {
            cleanup();
            this.loadFailed = true;
            reject(new Error('Chatwoot SDK not available after script load'));
          }
        };

        document.head.appendChild(script);
      });
    });
  }

  setUser(identifier: string, options: {
    name?: string;
    email?: string;
    identifier_hash?: string;
    [key: string]: any;
  }): void {
    if (this.isLoaded) {
      this.ngZone.runOutsideAngular(() => {
        window.$chatwoot.setUser(identifier, options);
      });
    }
  }

  setCustomAttributes(attrs: Record<string, any>): void {
    if (this.isLoaded) {
      this.ngZone.runOutsideAngular(() => {
        window.$chatwoot.setCustomAttributes(attrs);
      });
    }
  }

  setLocale(locale: string): void {
    if (this.isLoaded) {
      this.ngZone.runOutsideAngular(() => {
        window.$chatwoot.setLocale(locale);
      });
    }
  }

  toggle(state?: 'open' | 'close'): void {
    if (this.isLoaded) {
      this.ngZone.runOutsideAngular(() => {
        window.$chatwoot.toggle(state);
      });
    }
  }

  reset(): void {
    if (this.isLoaded) {
      this.ngZone.runOutsideAngular(() => {
        window.$chatwoot.reset();
      });
    }
  }
}
